import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';

import { analytics } from '@/analytics/analytics-service';
import {
  calculateNavigationIds,
  equipmentPoolAtom,
} from '@/features/equipment-browser/equipment-details/state/selected-equipment.state';
import {
  EquipmentDetailsHostRouteId,
  EquipmentDetailsRouteSearch,
  isEquipmentDetailsHostRouteId,
} from '@/utils/equipment-details-route-params';

export type DeviceDetailsAnalyticsAttributes = {
  'Device Kind'?: string;
  'Device Alias'?: string;
};

type EquipmentDetailsRouteMatch = {
  routeId?: string;
  search?: EquipmentDetailsRouteSearch;
};

type OpenEquipmentDetailsOptions = {
  equipmentId: string;
  sectionId?: string;
  viewId?: string;
  analyticsAttributes?: DeviceDetailsAnalyticsAttributes;
};

type EquipmentDetailsSearchUpdate = {
  equipmentId?: string;
  sectionId?: string;
  viewId?: string;
};

const getActiveEquipmentDetailsMatch = (
  matches: EquipmentDetailsRouteMatch[]
): EquipmentDetailsRouteMatch | undefined => {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const match = matches[index];
    if (isEquipmentDetailsHostRouteId(match?.routeId)) {
      return match;
    }
  }

  return undefined;
};

const warnUnsupportedRoute = (action: string, routeId?: string) => {
  if (import.meta.env.DEV) {
    console.warn(`[EquipmentDetails] Ignoring ${action} outside supported host routes.`, {
      routeId,
    });
  }
};

export const useEquipmentDetailsRouteState = () => {
  const navigate = useNavigate();
  const equipmentPool = useAtomValue(equipmentPoolAtom);
  const routeState = useRouterState({
    select: (state) => {
      const matches = (state.matches ?? []) as EquipmentDetailsRouteMatch[];
      const activeMatch = getActiveEquipmentDetailsMatch(matches);
      const leafMatch = matches[matches.length - 1];

      return {
        hostRouteId: activeMatch?.routeId as EquipmentDetailsHostRouteId | undefined,
        leafRouteId: leafMatch?.routeId,
        search: activeMatch?.search ?? {},
      };
    },
  });

  const search = routeState.search;
  const isDetailsOpen = Boolean(search.equipmentId);

  const updateEquipmentDetailsSearch = useCallback(
    async (updates: EquipmentDetailsSearchUpdate) => {
      if (!routeState.hostRouteId) {
        warnUnsupportedRoute('updateEquipmentDetailsSearch', routeState.leafRouteId);
        return false;
      }

      const nextSearch: EquipmentDetailsRouteSearch = {
        ...search,
        ...updates,
      };

      await navigate({
        replace: true,
        search: nextSearch as never,
      });

      return true;
    },
    [navigate, routeState.hostRouteId, routeState.leafRouteId, search]
  );

  const openEquipmentDetails = useCallback(
    async ({
      equipmentId,
      sectionId,
      viewId,
      analyticsAttributes,
    }: OpenEquipmentDetailsOptions) => {
      if (!routeState.hostRouteId) {
        warnUnsupportedRoute('openEquipmentDetails', routeState.leafRouteId);
        return false;
      }

      const wasOpen = Boolean(search.equipmentId);
      const nextSearch: EquipmentDetailsRouteSearch = {
        viewId: viewId ?? search.viewId,
        sectionId: sectionId ?? search.sectionId,
        equipmentId,
      };

      await navigate({
        replace: true,
        search: nextSearch as never,
      });

      if (!wasOpen) {
        analytics.track('Device Details Opened', {
          ...analyticsAttributes,
        });
      }

      return true;
    },
    [navigate, routeState.hostRouteId, routeState.leafRouteId, search]
  );

  const closeEquipmentDetails = useCallback(async () => {
    if (!routeState.hostRouteId) {
      warnUnsupportedRoute('closeEquipmentDetails', routeState.leafRouteId);
      return false;
    }

    await navigate({
      replace: true,
      search: {
        viewId: search.viewId,
        sectionId: search.sectionId,
        equipmentId: undefined,
      } as never,
    });

    return true;
  }, [navigate, routeState.hostRouteId, routeState.leafRouteId, search]);

  const { nextEquipmentId, prevEquipmentId } = useMemo(
    () => calculateNavigationIds(search.equipmentId, equipmentPool),
    [equipmentPool, search.equipmentId]
  );

  const selectNextEquipment = useCallback(async () => {
    if (!nextEquipmentId) return false;
    return openEquipmentDetails({ equipmentId: nextEquipmentId });
  }, [nextEquipmentId, openEquipmentDetails]);

  const selectPreviousEquipment = useCallback(async () => {
    if (!prevEquipmentId) return false;
    return openEquipmentDetails({ equipmentId: prevEquipmentId });
  }, [prevEquipmentId, openEquipmentDetails]);

  return {
    closeEquipmentDetails,
    equipmentId: search.equipmentId,
    hostRouteId: routeState.hostRouteId,
    isDetailsOpen,
    nextEquipmentId,
    openEquipmentDetails,
    prevEquipmentId,
    sectionId: search.sectionId,
    selectNextEquipment,
    selectPreviousEquipment,
    updateEquipmentDetailsSearch,
    viewId: search.viewId,
  };
};
